import React from 'react';
import { Box, VStack, HStack, Text, Icon } from '@chakra-ui/react';
import { FaCheck } from 'react-icons/fa6';
import { FiArrowRight } from 'react-icons/fi';
import { Tariff, TariffFeature } from '../../config/tariffs';
import { useTranslation } from '../../hooks/useTranslation';
import { useSupportEmail } from '../../hooks/useSupportEmail';
import Button from '../base/Button';

interface TariffCardProps {
    tariff: Tariff;
    isCurrent?: boolean;
    previousTariffName?: string;
    features?: TariffFeature[]; // Если передан, используем эти фичи вместо всех фичей тарифа
}

const TariffCard: React.FC<TariffCardProps> = ({
    tariff,
    isCurrent = false,
    previousTariffName,
    features,
}) => {
    const { t } = useTranslation();
    const { sendEmail } = useSupportEmail();
    const displayFeatures = features || tariff.features;

    const handleGoToTariff = () => {
        // Заменяем {tariff_name} на название тарифа, {customer_id} будет заменен в useSupportEmail
        const subject = t.pages.payment.tariffUpgradeSubject.replace('{tariff_name}', tariff.name);
        const body = t.pages.payment.tariffUpgradeBody.replace('{tariff_name}', tariff.name);
        sendEmail(subject, body);
    };
    const formatFeatureValue = (value: string | number | undefined): string => {
        if (value === undefined) return '';
        if (value === 'Unlimited') return 'Unlimited';
        if (typeof value === 'number') return value.toString();
        return value;
    };

    const renderFeature = (feature: TariffFeature, index: number) => {
        const hasValue = feature.value !== undefined;
        const valueText = formatFeatureValue(feature.value);
        // Получаем перевод по label, убирая префикс "tariff."
        const labelKey = feature.label.replace('tariff.', '');
        const featureText =
            t.pages.payment.tariffFeatures[labelKey as keyof typeof t.pages.payment.tariffFeatures] ||
            feature.label;

        // Если это дополнение к предыдущему тарифу (features передан), делаем текст жирным
        const isAdditionalFeature = !!features;

        return (
            <HStack key={index} gap={2} align="flex-start">
                <Icon as={FaCheck} boxSize={5} color="green.500" mt={0.5} flexShrink={0} />
                <Text
                    fontSize="md"
                    color="black"
                    flex={1}
                    fontWeight={isAdditionalFeature ? 'bold' : 'normal'}
                >
                    {featureText}
                    {hasValue && (
                        <Text as="span" fontWeight="bold" color="yellow.600" ml={1}>
                            {valueText}
                        </Text>
                    )}
                </Text>
            </HStack>
        );
    };

    return (
        <Box
            p={6}
            bg="white"
            border={isCurrent ? '2px solid' : undefined}
            borderColor={isCurrent ? 'yellow.600' : undefined}
            position="relative"
        >
            {isCurrent && (
                <Box
                    position="absolute"
                    top={-4}
                    left={6}
                    bg="yellow.600"
                    color="white"
                    px={3}
                    py={1}
                    fontSize="md"
                    fontWeight="bold"
                >
                    {t.pages.payment.currentTariff}
                </Box>
            )}
            <VStack gap={2} align="stretch">
                {/* Цена и название */}
                <VStack gap={2} align="center">
                    {/* Цена - серым, по центру, над названием */}
                    <Text fontSize="lg" color="gray.600" fontStyle="italic" textAlign="center">
                        {t.pages.payment.fromPrice} €{tariff.price} {t.pages.payment.perMonth}
                    </Text>

                    {/* Название тарифа - желтым, больше, по центру */}
                    <Text fontSize="2xl" fontWeight="bold" color="yellow.600" textAlign="center">
                        {tariff.name}
                    </Text>

                    {/* Подпись без НДС - серым, после названия с отступом */}
                    <Text fontSize="sm" color="gray.600" textAlign="center" mt={2}>
                        {t.pages.payment.withoutVat}
                    </Text>
                </VStack>

                {/* Что входит в тариф */}
                <VStack gap={3} align="stretch">
                    {previousTariffName ? (
                        <>
                            <Text fontSize="md" color="black" fontWeight="medium">
                                {t.pages.payment.sameAsPreviousTariff.replace(
                                    '{tariffName}',
                                    previousTariffName,
                                )}
                            </Text>
                            {displayFeatures.map((feature, index) => renderFeature(feature, index))}
                        </>
                    ) : (
                        displayFeatures.map((feature, index) => renderFeature(feature, index))
                    )}
                </VStack>

                {/* Кнопка "Перейти" для неактивных тарифов */}
                {!isCurrent && (
                    <Box mt={4}>
                        <Button buttonType="success" onClick={handleGoToTariff} w="full">
                            <HStack gap={2} justify="center">
                                <Text>{t.pages.payment.goToTariff}</Text>
                                <Icon as={FiArrowRight} boxSize={4} />
                            </HStack>
                        </Button>
                    </Box>
                )}
            </VStack>
        </Box>
    );
};

export default TariffCard;
